'use client';

import * as React from 'react';
import { HexColorPicker } from 'react-colorful';

import { cn } from '../../lib/utils';

interface ColorInputProps {
    value: string;
    onChange: (value: string) => void;
    id?: string;
    className?: string;
}

// Kalshi logo colors (brandkit) + black/white for one-tap selection.
const PRESETS = ['#00CE8E', '#00DD94', '#01201A', '#000000', '#FFFFFF'];

const isHex = (v: string) => /^#[0-9a-fA-F]{6}$/.test(v);

/**
 * Logo-color control: a hex field with a live swatch that opens a styled gradient
 * picker (react-colorful) + brand preset swatches. Click-outside / Escape closes.
 * Deliberately not the native `<input type="color">` (its OS popover looks foreign).
 */
export function ColorInput({ value, onChange, id, className }: ColorInputProps) {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef<HTMLDivElement>(null);
    const hex = value.replace(/^#/, '').toUpperCase();

    React.useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div ref={rootRef} className={cn('relative', className)}>
            <div className="flex h-9 items-center gap-2 rounded-x30 border border-stroke-x30 bg-surface-x10 px-2.5 transition-colors hover:border-text-x30 focus-within:border-stroke-x10">
                <button
                    type="button"
                    onClick={() => setOpen((o) => !o)}
                    aria-label="Open color picker"
                    aria-expanded={open}
                    className="h-5 w-5 shrink-0 rounded-full border border-stroke-x30 transition-transform active:scale-95"
                    style={{ backgroundColor: isHex(value) ? value : 'transparent' }}
                />
                <span className="font-mono text-xs text-text-x30">#</span>
                <input
                    id={id}
                    value={hex}
                    spellCheck={false}
                    maxLength={6}
                    onChange={(e) => onChange('#' + e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6))}
                    className="typ-body-x30 -ml-1 w-full bg-transparent font-mono uppercase text-text-x10 outline-none"
                />
            </div>

            {open ? (
                <div className="absolute left-0 top-[calc(100%+8px)] z-50 w-[232px] rounded-x30 border border-stroke-x30 bg-surface-x10 p-3 shadow-x20">
                    <HexColorPicker
                        color={isHex(value) ? value : '#000000'}
                        onChange={onChange}
                        style={{ width: '100%', height: 168 }}
                    />
                    <div className="mt-3 flex flex-wrap gap-2">
                        {PRESETS.map((c) => {
                            const active = value.toLowerCase() === c.toLowerCase();
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => onChange(c)}
                                    aria-label={`Use ${c}`}
                                    aria-pressed={active}
                                    className={cn(
                                        'h-6 w-6 rounded-full border transition-shadow',
                                        active
                                            ? 'border-text-x10 ring-2 ring-text-x10/25'
                                            : 'border-stroke-x30 hover:border-text-x30',
                                    )}
                                    style={{ backgroundColor: c }}
                                />
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
